import { create } from 'zustand';
import { supabase } from '@/utils/supabase';

export type AssignmentStrategy = 'claiming' | 'round_robin';

interface AssignmentConfig {
  strategy: AssignmentStrategy;
  timerMinutes: number;      // for round-robin: time before reassign/escalate
  enableAutoAssign: boolean; // for round-robin: assign immediately on order creation
}

interface AssignmentStrategyStore {
  config: AssignmentConfig;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadConfig: () => Promise<void>;
  setConfig: (config: AssignmentConfig) => Promise<void>;
  getTimerMs: () => number;
  isClaimingMode: () => boolean;
  isRoundRobinMode: () => boolean;
}

const STORAGE_KEY = 'chocolate_vibes_assignment_config';

const DEFAULT_CONFIG: AssignmentConfig = {
  strategy: 'round_robin',
  timerMinutes: 15,
  enableAutoAssign: true,
};

function loadFromStorage(): AssignmentConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_CONFIG };
}

function saveToStorage(config: AssignmentConfig) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch { /* ignore */ }
}

export const useAssignmentStrategyStore = create<AssignmentStrategyStore>((set, get) => ({
  config: { ...DEFAULT_CONFIG },
  isLoading: false,
  error: null,

  loadConfig: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'assignment_strategy')
        .single();

      if (!error && data?.value) {
        const parsed = JSON.parse(data.value);
        const merged = { ...DEFAULT_CONFIG, ...parsed };
        saveToStorage(merged);
        set({ config: merged, isLoading: false });
        return;
      }
    } catch (err) {
      // Supabase may not be configured or table doesn't exist — fall back to localStorage
    }
    // Fallback: use localStorage only when DB is unavailable
    set({ config: loadFromStorage(), isLoading: false });
  },

  setConfig: async (newConfig) => {
    set({ isLoading: true, error: null });
    try {
      // Save to localStorage first (always works)
      saveToStorage(newConfig);

      // Try to save to Supabase if configured
      await supabase.from('app_settings').upsert({
        key: 'assignment_strategy',
        value: JSON.stringify(newConfig),
      });
    } catch (err) {
      // Supabase not available — localStorage is already saved
    }
    set({ config: newConfig, isLoading: false });
  },

  getTimerMs: () => get().config.timerMinutes * 60 * 1000,
  isClaimingMode: () => get().config.strategy === 'claiming',
  isRoundRobinMode: () => get().config.strategy === 'round_robin',
}));

// Auto-load on first access
let loaded = false;
export function ensureAssignmentConfigLoaded() {
  if (!loaded) {
    loaded = true;
    useAssignmentStrategyStore.getState().loadConfig();
  }
}
